import { useQuery } from '@tanstack/react-query';
import { Controller, useFormContext } from 'react-hook-form';
import {
  MultiChainDWalletService,
  multiChainDWalletServiceKeys,
} from 'api/services/httpServices/DWalletService';
import { FormValues } from '../schema';
import { formatTokenAmount } from 'utils/FormatNumber';
import {
  MobileExpandCardTrigger,
  MobileExpandableCard,
} from 'modules/shared-components/MobileExpandableCard';

import {
  ArrowPathIcon,
  CheckIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline';

import { Combobox } from '@headlessui/react';
import classNames from 'classnames';
import { useState } from 'react';
import Button from 'modules/shared-components/button/button';
import { Link } from 'react-router-dom';
import { WalletBelowLimitWarning } from '../components/TradeWarnings';
import { chainAsset, chainAssetImg } from 'api/types/httpsTypes/d-wallets';
import { EthAddress } from 'modules/ethereum/components/EthAddress';
import { twMerge } from 'tailwind-merge';
import { screenGte, useMediaQuery } from 'modules/media/use-media-query';
import { useUserState } from 'modules/user/UserContext';
import { DexCard } from '../../components/cards';
import {
  DropdownButton,
  DropdownOption,
  DropdownOptionItem,
  Options,
  useDropdownPosition,
} from '../../components/dropdown';
import { ComboboxSearch, CountBadge } from '../../components/filters';
import { NotificationDex } from '../../components/alerts/notification';
import { SelectionModalOptions } from '../../components/modal/SelectionModal';
import { MobileOption } from 'modules/shared-components/forms/select/select-option';
import { Tabs } from '../../components/layout/SIDEBAR_CONST';

export function SelectDWallet({ disabled }: { disabled: boolean }) {
  const {
    control,
    watch,
    setValue,
    formState: { errors: formErrors },
  } = useFormContext<FormValues>();

  const { user } = useUserState();
  const selectedWallets = watch('wallets') || [];
  const chain = watch('chain');

  const { isLoading, data, error } = useQuery({
    queryFn: () => MultiChainDWalletService.getWallets(chain),
    queryKey: multiChainDWalletServiceKeys.getWallets(chain),
    enabled: !!chain && !!user,
  });

  const selectedWallet =
    selectedWallets && selectedWallets.length > 0
      ? data?.find((w) => w.id === selectedWallets[0])
      : null;

  const isDesktop = useMediaQuery(screenGte.small);

  return !user ? (
    <></>
  ) : (
    <MobileExpandableCard
      mobileLabel="Wallet(s)"
      trigger={
        <MobileExpandCardTrigger>
          {error ? (
            <div className="text-red-500">Error loading wallets...</div>
          ) : data?.length == 0 ? (
            <div className="space-y-1 text-sm">
              {!formErrors.wallets ? (
                <p className="mb-0 xxs:text-xs xxs:text-dex-white-secondary">
                  Create a wallet and start to snipe
                </p>
              ) : (
                <div className="flex space-x-1 items-center">
                  <ExclamationCircleIcon className="xxs:text-red-500 w-4 h-4" />
                  <p className="text-red-500 mb-0 xxs:font-semibold xxs:text-base">
                    Create a wallet first
                  </p>
                </div>
              )}{' '}
            </div>
          ) : (
            <div className="flex xxs:gap-2 items-center">
              <div className="text-sm">
                {selectedWallet ? (
                  <div className="flex space-x-2 items-center">
                    <div className="items-center flex space-x-1">
                      <div>Wallet(s):</div>
                      <img
                        src={chainAssetImg(chain)}
                        width={10}
                        alt="Token logo"
                      />
                      <div className="xxs:text-dex-white">
                        {formatTokenAmount(selectedWallet.chainBalance)}
                      </div>
                      <div>|</div>
                      <div className="truncate max-w-[140px]">
                        {selectedWallet.label}
                      </div>
                    </div>
                    {selectedWallets?.length > 1 && (
                      <CountBadge>+{selectedWallets?.length - 1}</CountBadge>
                    )}
                  </div>
                ) : (
                  <div className="xxs:text-sm lg:text-xs xxs:dex-text-white-secondary">
                    Select one or more wallet(s)
                  </div>
                )}
              </div>
              {formErrors.wallets && (
                <ExclamationCircleIcon className="xxs:text-red-500 xxs:w-6 h-6" />
              )}
            </div>
          )}
        </MobileExpandCardTrigger>
      }
    >
      {!isDesktop ? (
        <SelectionModalOptions className="lg:hidden" close={() => {}}>
          {!user ? (
            <Button
              variant="primary"
              as={Link}
              to="/login"
              className="xxs:text-xs xxs:p-1"
            >
              <span className="text-xs">login </span>
            </Button>
          ) : error ? (
            <div className="text-red-500">Error loading wallets...</div>
          ) : data?.length === 0 ? (
            <div className="flex">
              <Button
                className="w-full m-2"
                variant="primary"
                as={Link}
                to={`/dex/wallets/${Tabs.Portfolio}`}
              >
                CREATE WALLET
              </Button>
            </div>
          ) : (
            <div className="xxs:space-y-2">
              <div className="xxs:mx-2 lg:mx-0">
                <WalletBelowLimitWarning />
              </div>

              {(data || []).map((option) => (
                <MobileOption
                  key={option.id}
                  value={option.id}
                  label={
                    <div className="flex items-center justify-between w-full overflow-hidden xxs:gap-8">
                      <div className="flex flex-col w-[200px]">
                        <span className="xxs:text-base truncate xxs:text-dex-white-secondary">
                          {option.label}
                        </span>
                        <span className="xxs:text-xs">
                          <EthAddress
                            address={option.address}
                            chain={chain}
                            kind="account"
                          />
                        </span>
                      </div>
                      <div className="mr-auto xxs:text-base truncate xxs:font-semibold flex space-x-2 items-center">
                        <img
                          src={chainAssetImg(chain)}
                          width={10}
                          alt="Token logo"
                        />
                        <div className="xxs:text-dex-white">
                          {formatTokenAmount(option.chainBalance)}
                        </div>
                      </div>
                      {selectedWallets?.includes(option.id) && (
                        <CheckIcon className="h-5 w-5 xxs:text-dex-white" />
                      )}
                    </div>
                  }
                  onClick={() => {
                    const updatedWallets = selectedWallets?.includes(option.id)
                      ? selectedWallets?.filter(
                          (walletId) => walletId !== option.id
                        )
                      : [...selectedWallets, option.id];
                    setValue('wallets', updatedWallets);
                  }}
                />
              ))}
            </div>
          )}
        </SelectionModalOptions>
      ) : (
        ''
      )}
      <DexCard
        as="div"
        size="xs"
        disabled={disabled}
        className="xxs:hidden lg:flex"
      >
        <div className="flex justify-between">
          <div className="font-bold text-sm">Wallet</div>
          {data &&
            data.length > 2 &&
            data.length !== watch('wallets').length && (
              <button
                onClick={() =>
                  setValue(
                    'wallets',
                    data?.map((wallet) => wallet.id)
                  )
                }
                className="text-blue-500 hover:text-blue-600"
              >
                Select All
              </button>
            )}
          {data &&
            data.length > 2 &&
            data.length === watch('wallets').length && (
              <button
                onClick={() => setValue('wallets', [])}
                className="text-blue-500 hover:text-blue-600"
              >
                Remove All
              </button>
            )}
        </div>

        {!user ? (
          <Button
            variant="primary"
            as={Link}
            to="/login"
            className="xxs:text-xs xxs:p-1"
          >
            <span className="text-xs">login </span>
          </Button>
        ) : error ? (
          <div className="text-red-500">Error loading wallets...</div>
        ) : data?.length == 0 ? (
          <div className="space-y-1">
            {!formErrors.wallets ? (
              <p className="mb-0 xxs:text-dex-white-secondary">
                Create a wallet and start to snipe
              </p>
            ) : (
              <div className="flex space-x-1 items-center">
                <ExclamationCircleIcon className="xxs:text-red-500 w-4 h-4" />
                <p className="text-red-500 mb-0">Create a wallet first</p>
              </div>
            )}
          </div>
        ) : (
          <>
            <Controller
              name="wallets"
              control={control}
              render={({ field }) => {
                return (
                  <>
                    <WalletsDropdown
                      {...field}
                      size="xs"
                      label=""
                      options={
                        data?.map((wallet) => ({
                          label: `${formatTokenAmount(
                            wallet.chainBalance
                          )} ${chainAsset(chain)} | ${wallet.label}`,
                          value: wallet.id,
                        })) || []
                      }
                      onChange={(value) => {
                        field.onChange(value);
                      }}
                      loading={isLoading}
                    />
                    {formErrors.wallets && (
                      <NotificationDex type="error">
                        Select a wallet to snipe
                      </NotificationDex>
                    )}
                  </>
                );
              }}
            />
            {<WalletBelowLimitWarning />}
          </>
        )}
      </DexCard>
    </MobileExpandableCard>
  );
}

export type FilterDropdownProps<T extends string | number> = {
  label: string;
  options?: DropdownOptionItem<T>[];
  allOptions?: DropdownOptionItem<T>[];
  value: T[];
  onChange: (value: T[]) => void;
  onOpen?: () => void;
  icon?: React.ReactNode;
  isError?: boolean;
  search?: React.ReactNode;
  loading?: boolean;
  size?: string;
};

export function WalletsDropdown<T extends string | number>({
  options,
  onChange,
  size,
  ...rest
}: Omit<FilterDropdownProps<T>, 'query' | 'setQuery' | 'isLoading'>) {
  const [query, setQuery] = useState('');

  const availableOptions =
    query === ''
      ? options
      : (options || []).filter((option) =>
          option.label.toLowerCase().includes(query.toLowerCase())
        );

  return (
    <FilterDropdown
      {...rest}
      onChange={(val) => {
        onChange(val);
        setQuery('');
      }}
      size={size}
      search={<ComboboxSearch size={size} query={query} setQuery={setQuery} />}
      allOptions={options}
      options={availableOptions}
    />
  );
}

export function FilterDropdown<T extends string | number>({
  label,
  options,
  allOptions,
  value = [],
  onChange,
  isError,
  search,
  onOpen,
  loading,
  size,
}: FilterDropdownProps<T>) {
  const { setReferenceElement, setPopperElement, attributes, styles } =
    useDropdownPosition();

  const firstWallet =
    !!value.length && value.length > 0 && allOptions
      ? allOptions?.find((o) => o.value === value[0])?.label
      : '';

  return (
    <Combobox
      as="div"
      value={value}
      onChange={(newValue: T[]) => {
        onChange(newValue);
      }}
      ref={setReferenceElement}
      className={twMerge(
        'relative xxs:flex-shrink-0 ',
        size === 'xs' ? 'xxs:flex' : ''
      )}
      multiple
    >
      <DropdownButton
        className={twMerge(
          'xxs:bg-dex-black-800 border-tailwind xxs:border-solid xxs:border-dex-black-700',
          size === 'xs' ? 'xxs:h-8  ' : ''
        )}
        as={Combobox.Button}
        onClick={onOpen}
      >
        {!!value.length && value.length === 1 ? (
          <span className="max-w-[150px] text-xs truncate">{firstWallet}</span>
        ) : !!value.length && value.length > 1 ? (
          <div className="flex space-x-2 items-center">
            <div className="max-w-[150px] text-xs truncate">{firstWallet}</div>
            <CountBadge
              className={size === 'xs' ? 'xxs:px-1.5 xxs:py-0 text-xs ' : ''}
            >
              <div className={size === 'xs' ? 'text-xs' : ''}>
                +{value.length - 1}
              </div>
            </CountBadge>
          </div>
        ) : (
          <span className="xxs:text-sm lg:text-xs xxs:dex-text-white-secondary">
            Select one or more wallet(s)
          </span>
        )}
        <span className="xxs:flex-grow text-xs">{label}</span>
      </DropdownButton>

      <Options
        as={Combobox.Options}
        {...attributes.popper}
        ref={setPopperElement}
        style={styles.popper}
      >
        {search}
        {loading && (
          <Combobox.Option
            value={null}
            disabled
            className="xxs:py-2 xxs:px-2 flex justify-center"
          >
            <ArrowPathIcon
              className={classNames(
                'h-4 w-4 xxs:text-dex-white-secondary animate-spin'
              )}
              aria-hidden="true"
            />
          </Combobox.Option>
        )}
        {isError && (
          <Combobox.Option
            value={null}
            disabled
            className="xxs:py-2 xxs:pl-3 xxs:pr-10 text-red-500 text-xs"
          >
            Could not load options
          </Combobox.Option>
        )}
        {options?.length === 0 && (
          <Combobox.Option
            value={null}
            disabled
            className="xxs:py-2 xxs:pl-3 xxs:pr-10 text-white-800 text-xs"
          >
            No results found
          </Combobox.Option>
        )}
        {options?.map((option, index) => (
          <DropdownOption
            size={size}
            as={Combobox.Option}
            key={option.value}
            option={option}
            index={index}
          />
        ))}
      </Options>
    </Combobox>
  );
}
